import React, { useState, useEffect } from "react";
import {
    Box, Typography, TextField, Button, Avatar, IconButton,
    Dialog, DialogActions, DialogContent, DialogTitle, Slider, MenuItem,
    Stack, Chip, Skeleton, Tooltip, Container, Alert, InputAdornment
} from '@mui/material';
import EditIcon             from '@mui/icons-material/Edit';
import SaveIcon             from '@mui/icons-material/Save';
import CloseIcon            from '@mui/icons-material/Close';
import CameraAltIcon        from '@mui/icons-material/CameraAlt';
import PersonIcon           from '@mui/icons-material/Person';
import EmailIcon            from '@mui/icons-material/Email';
import PhoneIcon            from '@mui/icons-material/Phone';
import BadgeIcon            from '@mui/icons-material/Badge';
import PublicIcon           from '@mui/icons-material/Public';
import TranslateIcon        from '@mui/icons-material/Translate';
import LocationOnIcon       from '@mui/icons-material/LocationOn';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import LocationCityIcon       from '@mui/icons-material/LocationCity';
import CircularProgress     from '@mui/material/CircularProgress';
import Cropper              from 'react-easy-crop';
import { useNavigate }      from "react-router-dom";
import { useTranslation }   from "react-i18next";
import i18n                 from "../i18n";
import axios      from "../api/axiosConfig";
import { useAuth } from "../context/AuthContext";
import ChangePasswordDialog from "./ChangePasswordDialog";
import LockResetIcon  from "@mui/icons-material/LockReset";
import BlockIcon      from "@mui/icons-material/Block";
import Visibility     from "@mui/icons-material/Visibility";
import VisibilityOff  from "@mui/icons-material/VisibilityOff";

const TERRACOTTA = '#C4622D';
const FOREST     = '#4A6741';
const GOLD       = '#C9973A';
const WALNUT     = '#2C1F14';
const PARCHMENT  = '#F7F3EE';
const STONE      = '#EDE8E1';

const COUNTRIES = ["Armenia", "Spain", "USA", "France", "Germany", "UK", "Russia", "Other"];
const SUPPORTED_CODES = new Set(['en', 'ru', 'es']);

// ── Inline editable field ─────────────────────────────────────────────────────
function EditableField({ label, value, icon, canEdit, onSave, type = 'text', selectOptions = null, multiline = false }) {
    const [editing, setEditing] = useState(false);
    const [draft,   setDraft]   = useState(value || '');
    const [saving,  setSaving]  = useState(false);
    const { t } = useTranslation();

    useEffect(() => { setDraft(value || ''); }, [value]);

    const handleSave = async () => {
        setSaving(true);
        await onSave(draft);
        setSaving(false);
        setEditing(false);
    };
    const handleCancel = () => { setDraft(value || ''); setEditing(false); };

    return (
        <Box sx={{
            py: 2.5, px: 3, display: 'flex', alignItems: 'flex-start', gap: 2,
            borderBottom: '1px solid', borderColor: 'rgba(196,98,45,0.1)',
            transition: 'background 0.2s',
            '&:hover': { bgcolor: canEdit ? 'rgba(196,98,45,0.03)' : 'transparent' },
        }}>
            <Box sx={{ color: TERRACOTTA, mt: editing ? 1.2 : 0.3, flexShrink: 0 }}>{icon}</Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600,
                    textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: '0.7rem' }}>
                    {label}
                </Typography>
                {editing ? (
                    <Stack direction="row" spacing={1} alignItems="center" mt={0.5}>
                        {selectOptions ? (
                            <TextField select size="small" value={draft}
                                       onChange={e => setDraft(e.target.value)} sx={{ minWidth: 160 }}>
                                {selectOptions.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                            </TextField>
                        ) : (
                            <TextField size="small" value={draft} type={type}
                                       onChange={e => setDraft(e.target.value)}
                                       multiline={multiline} rows={multiline ? 3 : 1}
                                       sx={{ flex: 1 }}
                                       onKeyDown={e => {
                                           if (e.key === 'Enter' && !multiline) handleSave();
                                           if (e.key === 'Escape') handleCancel();
                                       }}
                                       autoFocus />
                        )}
                        <Tooltip title={t('common.save')}>
                            <IconButton size="small" color="success" onClick={handleSave} disabled={saving}>
                                {saving ? <CircularProgress size={14} /> : <SaveIcon fontSize="small" />}
                            </IconButton>
                        </Tooltip>
                        <Tooltip title={t('common.cancel')}>
                            <IconButton size="small" onClick={handleCancel}><CloseIcon fontSize="small" /></IconButton>
                        </Tooltip>
                    </Stack>
                ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.3 }}>
                        <Typography variant="body1" fontWeight={600} sx={{ color: value ? WALNUT : 'text.disabled' }}>
                            {value || '—'}
                        </Typography>
                        {canEdit && (
                            <Tooltip title={`${t('common.edit')} ${label}`}>
                                <IconButton size="small" onClick={() => setEditing(true)}
                                            sx={{ color: 'text.disabled', opacity: 0,
                                                '.MuiBox-root:hover &': { opacity: 1 },
                                                '&:hover': { color: TERRACOTTA }, transition: 'opacity 0.2s' }}>
                                    <EditIcon sx={{ fontSize: '0.9rem' }} />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>
                )}
            </Box>
        </Box>
    );
}

// ── Preferences skeleton ──────────────────────────────────────────────────────
function PreferencesSkeleton() {
    return (
        <>
            {[60, 80].map((w, i) => (
                <Box key={i} sx={{ py: 2.5, px: 3, display: 'flex', alignItems: 'center', gap: 2,
                    borderBottom: i === 0 ? '1px solid rgba(196,98,45,0.1)' : 'none' }}>
                    <Skeleton variant="circular" width={22} height={22} />
                    <Box sx={{ flex: 1 }}>
                        <Skeleton variant="text" width={w} height={14} />
                        <Skeleton variant="text" width={120} height={22} sx={{ mt: 0.5 }} />
                    </Box>
                </Box>
            ))}
        </>
    );
}

// ── Main ──────────────────────────────────────────────────────────────────────
const Profile = () => {
    const [pwdDialogOpen, setPwdDialogOpen] = useState(false);
    const { user, setUser } = useAuth();
    const navigate          = useNavigate();
    const { t }             = useTranslation();

    // Languages list (for dropdown)
    const [languagesList,   setLanguagesList]   = useState([]);
    // Customer-specific data (country only now)
    const [customerData,    setCustomerData]    = useState({ country: null });
    const [customerLoading, setCustomerLoading] = useState(true);

    // Guide-specific data (district name, stars, startDate)
    const [guideData, setGuideData] = useState(null);

    const [imageToCrop,     setImageToCrop]     = useState(null);
    const [crop,            setCrop]            = useState({ x: 0, y: 0 });
    const [zoom,            setZoom]            = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

    const canSeeRole      = user?.role === 'MANAGER' || user?.role === 'GUIDE' || user?.role === 'ADMIN';
    const isManager       = user?.role === 'MANAGER' || user?.role === 'ADMIN';
    const canDeactivate   = user?.role === 'CLIENT' || user?.role === 'MANAGER';

    const [deactivateOpen,    setDeactivateOpen]    = useState(false);
    const [deactivatePwd,     setDeactivatePwd]     = useState('');
    const [deactivatePwdShow, setDeactivatePwdShow] = useState(false);
    const [deactivateError,   setDeactivateError]   = useState('');
    const [deactivateLoading, setDeactivateLoading] = useState(false);

    const closeDeactivate = () => {
        setDeactivateOpen(false);
        setDeactivatePwd('');
        setDeactivateError('');
        setDeactivatePwdShow(false);
    };

    const confirmDeactivate = async () => {
        if (!deactivatePwd) { setDeactivateError(t('users.password_required')); return; }
        setDeactivateLoading(true);
        setDeactivateError('');
        try {
            await axios.post('/api/app_user/login', { email: user.email, password: deactivatePwd });
            await axios.patch(`/api/app_user/${user.id}/deactivate`);
            // Log the user out after deactivation
            await axios.post('/api/app_user/logout');
            navigate('/login');
        } catch (err) {
            const code = err.response?.data?.error;
            const st   = err.response?.status;
            setDeactivateError(
                st === 401 || code === 'WRONG_PASSWORD' || code === 'INVALID_CREDENTIALS'
                    ? t('users.wrong_password')
                    : t('profile.deactivate_failed', 'Failed to deactivate account.')
            );
        } finally {
            setDeactivateLoading(false);
        }
    };

    // Fetch all languages for the dropdown
    useEffect(() => {
        axios.get('/languages/view')
            .then(res => setLanguagesList(Array.isArray(res.data) ? res.data : (res.data?.content || [])))
            .catch(() => setLanguagesList([]));
    }, []);

    // Fetch customer data (country only — language is now on AppUser)
    useEffect(() => {
        if (!user?.id) return;
        axios.get(`/api/clients/view/id/${user.id}`)
            .then(res => setCustomerData({ country: res.data.country ?? null }))
            .catch(() => {})
            .finally(() => setCustomerLoading(false));
    }, [user?.id]);

    // Fetch guide data for GUIDE role
    useEffect(() => {
        if (user?.role !== 'GUIDE' || !user?.id) return;
        axios.get(`/guides/view/id/${user.id}`)
            .then(res => setGuideData(res.data))
            .catch(() => {});
    }, [user?.role, user?.id]);

    // Helpers
    const getLangLabel = (lang) => {
        if (!lang) return null;
        if (lang.language && lang.code) return `${lang.language} (${lang.code.toUpperCase()})`;
        return lang.language || lang.code || null;
    };

    // Current language: read languageId from user (AppUserDto field)
    const currentLanguage      = languagesList.find(l => l.id === user?.languageId) ?? null;
    const currentLanguageLabel = getLangLabel(currentLanguage);

    // ── Save AppUser fields (username, phone, idNumber, role, languageId) ─────
    // Always send ALL fields — backend overwrites unconditionally.
    const saveUserField = async (field, value) => {
        try {
            const payload = {
                username:   user.username  ?? '',
                phone:      user.phone     ?? '',
                idNumber:   user.idNumber  ?? '',
                role:       user.role      ?? '',
                removeImage: false,
                [field]: value,
            };
            const form = new FormData();
            form.append("user", new Blob([JSON.stringify(payload)], { type: "application/json" }));
            const res = await axios.put(`/api/app_user/edit/${user.id}`, form, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            setUser(prev => ({ ...prev, ...res.data }));
        } catch {
            alert(t('profile.save_failed'));
        }
    };

    // ── Save language — special case: also calls i18n.changeLanguage ──────────
    const saveLanguage = async (label) => {
        const lang = languagesList.find(l => getLangLabel(l) === label);
        if (!lang) return;
        try {
            const payload = {
                username:   user.username  ?? '',
                phone:      user.phone     ?? '',
                idNumber:   user.idNumber  ?? '',
                role:       user.role      ?? '',
                removeImage: false,
                languageId: lang.id,          // ← the only changing field
            };
            const form = new FormData();
            form.append("user", new Blob([JSON.stringify(payload)], { type: "application/json" }));
            const res = await axios.put(`/api/app_user/edit/${user.id}`, form, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            setUser(prev => ({ ...prev, ...res.data, languageId: lang.id }));

            // Switch UI language immediately (only for supported locales)
            const code = SUPPORTED_CODES.has(lang.code) ? lang.code : 'en';
            if (i18n.language !== code) await i18n.changeLanguage(code);
        } catch {
            alert(t('profile.save_failed'));
        }
    };

    // ── Save Customer fields (country only) ───────────────────────────────────
    const saveCustomerField = async (field, value) => {
        try {
            await axios.put(`/api/clients/edit/${user.id}`, { [field]: value });
            setCustomerData(prev => ({ ...prev, [field]: value }));
        } catch {
            alert(t('profile.save_failed'));
        }
    };

    // ── Profile image crop + upload ───────────────────────────────────────────
    const applyAndUploadCrop = async () => {
        const croppedFile = await getCroppedImg(imageToCrop, croppedAreaPixels);
        setImageToCrop(null);
        const payload = {
            username: user.username ?? '', phone: user.phone ?? '',
            idNumber: user.idNumber ?? '', role:  user.role  ?? '',
            removeImage: false,
        };
        const form = new FormData();
        form.append("user",  new Blob([JSON.stringify(payload)], { type: "application/json" }));
        form.append("image", croppedFile);
        try {
            const res = await axios.put(`/api/app_user/edit/${user.id}`, form, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            setUser(prev => ({ ...prev, imageUrl: res.data.imageUrl }));
        } catch {
            alert("Image upload failed.");
        }
    };

    if (!user) return null;

    return (
        <Box sx={{ minHeight: '100vh', background: `linear-gradient(rgba(247,243,238,0.84), rgba(247,243,238,0.84)), url("https://upload.wikimedia.org/wikipedia/commons/d/d5/Yerevan_Armenia_with_the_backdrop_of_Mount_Ararat.JPG") center/cover fixed` }}>
            {/* Banner */}
            <Box sx={{
                background: `linear-gradient(160deg, ${WALNUT} 0%, #6B3A20 50%, ${TERRACOTTA} 100%)`,
                height: 160, position: 'relative', overflow: 'hidden',
                '&::after': { content: '""', position: 'absolute', bottom: 0, left: 0, right: 0,
                    height: '40px', background: PARCHMENT, borderRadius: '40px 40px 0 0' }
            }} />

            <Container maxWidth="lg" sx={{ mt: -8, pb: 8, position: 'relative', zIndex: 1 }}>

                {/* Avatar + name row */}
                <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', sm: 'flex-end' }, gap: 3, mb: 5, flexWrap: 'wrap' }}>
                    <Box sx={{ position: 'relative' }}>
                        <Avatar src={user.imageUrl} alt={user.username}
                                sx={{ width: 100, height: 100, border: `4px solid ${PARCHMENT}`,
                                    fontSize: '2.5rem', boxShadow: '0 4px 20px rgba(44,31,20,0.18)',
                                    bgcolor: TERRACOTTA }}>
                            {user.username?.[0]?.toUpperCase()}
                        </Avatar>
                        <Tooltip title={t('profile.change_photo')}>
                            <IconButton size="small" component="label"
                                        sx={{ position: 'absolute', bottom: -4, right: -4,
                                            bgcolor: TERRACOTTA, color: 'white', width: 28, height: 28,
                                            '&:hover': { bgcolor: '#9A4A1F' },
                                            boxShadow: '0 2px 8px rgba(196,98,45,0.4)' }}>
                                <CameraAltIcon sx={{ fontSize: '0.9rem' }} />
                                <input type="file" hidden accept="image/*"
                                       onChange={e => {
                                           const f = e.target.files[0];
                                           if (f) setImageToCrop(URL.createObjectURL(f));
                                       }} />
                            </IconButton>
                        </Tooltip>
                    </Box>

                    <Box sx={{ flex: 1, mb: 1, minWidth: 0 }}>
                        <Typography variant="h4" fontWeight={900} noWrap sx={{ color: WALNUT, lineHeight: 1.1, fontFamily: "'Playfair Display', Georgia, serif", overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }}>
                            {user.username}
                        </Typography>
                        <Stack direction="row" spacing={1} mt={0.5} flexWrap="wrap" gap={0.5}>
                            {canSeeRole && (
                                <Chip label={user.role} size="small"
                                      sx={{ bgcolor: GOLD, color: WALNUT, fontWeight: 700, fontSize: '0.7rem' }} />
                            )}
                            <Chip label={user.email} size="small" variant="outlined"
                                  sx={{ borderColor: 'rgba(196,98,45,0.55)', color: TERRACOTTA,
                                      fontSize: '0.75rem', fontWeight: 600 }} />
                        </Stack>
                    </Box>

                    <Box sx={{ pb: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {user.role !== 'GUIDE' && (
                            <Button variant="contained" startIcon={<ConfirmationNumberIcon />}
                                    component="a" href="/my-bookings"
                                    sx={{ background: `linear-gradient(135deg, ${TERRACOTTA}, #9A4A1F)`,
                                        color: 'white', borderRadius: 2, fontWeight: 700,
                                        boxShadow: '0 4px 12px rgba(196,98,45,0.3)' }}>
                                {isManager ? t('profile.all_bookings') : t('profile.my_bookings')}
                            </Button>
                        )}
                        <Button variant="outlined" startIcon={<LockResetIcon />}
                                onClick={() => setPwdDialogOpen(true)}
                                sx={{ borderColor: "#C4622D", color: "#C4622D",
                                    "&:hover": { bgcolor: "rgba(196,98,45,0.06)" },
                                    borderRadius: 2, fontWeight: 700 }}>
                            {t('auth.change_password_btn')}
                        </Button>
                        {canDeactivate && (
                            <Button variant="outlined" startIcon={<BlockIcon />}
                                    onClick={() => setDeactivateOpen(true)}
                                    sx={{ borderColor: '#8B2020', color: '#8B2020',
                                        '&:hover': { bgcolor: 'rgba(139,32,32,0.06)' },
                                        borderRadius: 2, fontWeight: 700 }}>
                                {t('profile.deactivate_account', 'Deactivate Account')}
                            </Button>
                        )}
                    </Box>
                </Box>

                {/* Two-column grid */}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 4 }}>

                    {/* ── Personal Info ─────────────────────────────────────── */}
                    <Box sx={{ bgcolor: 'white', borderRadius: 3, overflow: 'hidden',
                        boxShadow: '0 2px 16px rgba(44,31,20,0.07)',
                        border: '1px solid rgba(196,98,45,0.1)' }}>
                        <Box sx={{ px: 3, py: 2.5, borderBottom: '1px solid rgba(196,98,45,0.1)',
                            display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PersonIcon sx={{ color: TERRACOTTA, fontSize: '1.2rem' }} />
                            <Typography variant="subtitle1" fontWeight={700} sx={{ color: WALNUT }}>
                                {t('profile.personal_info')}
                            </Typography>
                        </Box>

                        <EditableField label={t('profile.full_name')} value={user.username}
                                       icon={<PersonIcon />} canEdit
                                       onSave={v => saveUserField('username', v)} />
                        {/* Email read-only — requires Supabase Auth update */}
                        <EditableField label={t('profile.email')} value={user.email}
                                       icon={<EmailIcon />} canEdit={false} type="email"
                                       onSave={() => {}} />
                        <EditableField label={t('profile.phone')} value={user.phone}
                                       icon={<PhoneIcon />} canEdit type="tel"
                                       onSave={v => saveUserField('phone', v)} />
                        <EditableField label={t('profile.id_number')} value={user.idNumber}
                                       icon={<BadgeIcon />} canEdit
                                       onSave={v => saveUserField('idNumber', v)} />

                        {/* ── Language — now on AppUser, visible to ALL roles ── */}
                        <EditableField
                            label={t('profile.language')}
                            value={currentLanguageLabel}
                            icon={<TranslateIcon />}
                            canEdit
                            selectOptions={languagesList.map(getLangLabel).filter(Boolean)}
                            onSave={saveLanguage}
                        />
                    </Box>

                    {/* ── Preferences + Role ────────────────────────────────── */}
                    <Box>
                        {/* Preferences (country — CLIENT only, gracefully hidden if no customerData) */}
                        {user.role === 'CLIENT' && (
                            <Box sx={{ bgcolor: 'white', borderRadius: 3, overflow: 'hidden',
                                boxShadow: '0 2px 16px rgba(44,31,20,0.07)',
                                border: '1px solid rgba(196,98,45,0.1)', mb: 3 }}>
                                <Box sx={{ px: 3, py: 2.5, borderBottom: '1px solid rgba(196,98,45,0.1)',
                                    display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <PublicIcon sx={{ color: FOREST, fontSize: '1.2rem' }} />
                                    <Typography variant="subtitle1" fontWeight={700} sx={{ color: WALNUT }}>
                                        {t('profile.preferences')}
                                    </Typography>
                                </Box>
                                {customerLoading ? <PreferencesSkeleton /> : (
                                    <EditableField label={t('profile.country')} value={customerData.country}
                                                   icon={<LocationOnIcon />} canEdit
                                                   selectOptions={COUNTRIES}
                                                   onSave={v => saveCustomerField('country', v)} />
                                )}
                            </Box>
                        )}

                        {/* Guide assignment info */}
                        {user.role === 'GUIDE' && guideData && (
                            <Box sx={{ bgcolor: 'white', borderRadius: 3, overflow: 'hidden',
                                boxShadow: '0 2px 16px rgba(44,31,20,0.07)',
                                border: '1px solid rgba(196,98,45,0.1)', mb: 3 }}>
                                <Box sx={{ px: 3, py: 2.5, borderBottom: '1px solid rgba(196,98,45,0.1)',
                                    display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <LocationCityIcon sx={{ color: FOREST, fontSize: '1.2rem' }} />
                                    <Typography variant="subtitle1" fontWeight={700} sx={{ color: WALNUT }}>
                                        {t('profile.assignment', 'Assignment')}
                                    </Typography>
                                </Box>
                                <Box sx={{ px: 3, py: 1 }}>
                                    <Box sx={{ py: 1.5, borderBottom: '1px solid rgba(196,98,45,0.08)' }}>
                                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600,
                                            textTransform: 'uppercase', letterSpacing: '0.07em', fontSize: '0.68rem' }}>
                                            {t('profile.district', 'District')}
                                        </Typography>
                                        <Typography variant="body2" fontWeight={600} sx={{ color: WALNUT }}>
                                            {guideData.districtName || '—'}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ py: 1.5, borderBottom: '1px solid rgba(196,98,45,0.08)' }}>
                                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600,
                                            textTransform: 'uppercase', letterSpacing: '0.07em', fontSize: '0.68rem' }}>
                                            {t('profile.start_date', 'Start Date')}
                                        </Typography>
                                        <Typography variant="body2" fontWeight={600} sx={{ color: WALNUT }}>
                                            {guideData.startDate
                                                ? new Date(guideData.startDate).toLocaleDateString('en-GB',
                                                    { day: 'numeric', month: 'long', year: 'numeric' })
                                                : '—'}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ py: 1.5 }}>
                                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600,
                                            textTransform: 'uppercase', letterSpacing: '0.07em', fontSize: '0.68rem' }}>
                                            {t('profile.rating', 'Rating')}
                                        </Typography>
                                        <Typography variant="body2" fontWeight={600} sx={{ color: WALNUT }}>
                                            {guideData.stars != null
                                                ? `${Number(guideData.stars).toFixed(1)} / 5`
                                                : t('profile.no_rating', 'No rating yet')}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        )}

                        {/* Member since */}
                        <Box sx={{ px: 3, py: 2, bgcolor: STONE, borderRadius: 2,
                            border: '1px solid rgba(196,98,45,0.1)' }}>
                            <Typography variant="caption" color="text.secondary" display="block">
                                {t('profile.member_since')}
                            </Typography>
                            <Typography variant="body2" fontWeight={600} sx={{ color: WALNUT }}>
                                {user.createdAt
                                    ? new Date(user.createdAt).toLocaleDateString('en-GB',
                                        { day: 'numeric', month: 'long', year: 'numeric' })
                                    : '—'}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </Container>

            {/* Deactivate Account Dialog */}
            <Dialog open={deactivateOpen} onClose={closeDeactivate} fullWidth maxWidth="xs"
                    PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle fontWeight={700}
                             sx={{ fontFamily: "'Playfair Display', Georgia, serif", color: '#8B2020' }}>
                    {t('profile.deactivate_account', 'Deactivate Account')}
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {t('profile.deactivate_warning',
                            'Your account will be disabled immediately. Enter your password to confirm.')}
                    </Typography>
                    <TextField
                        fullWidth
                        label={t('users.your_password')}
                        type={deactivatePwdShow ? 'text' : 'password'}
                        value={deactivatePwd}
                        onChange={e => { setDeactivatePwd(e.target.value); setDeactivateError(''); }}
                        onKeyDown={e => { if (e.key === 'Enter') confirmDeactivate(); }}
                        autoFocus
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={() => setDeactivatePwdShow(p => !p)} edge="end">
                                        {deactivatePwdShow ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                    {deactivateError && <Alert severity="error" sx={{ mt: 1.5 }}>{deactivateError}</Alert>}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                    <Button onClick={closeDeactivate} disabled={deactivateLoading}>
                        {t('common.cancel')}
                    </Button>
                    <Button variant="contained" onClick={confirmDeactivate} disabled={deactivateLoading}
                            sx={{ bgcolor: '#8B2020', '&:hover': { bgcolor: '#6B1515' },
                                borderRadius: 2, fontWeight: 700 }}>
                        {deactivateLoading ? t('common.saving') : t('profile.deactivate_confirm', 'Deactivate')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Crop Dialog */}
            <Dialog open={!!imageToCrop} onClose={() => setImageToCrop(null)} fullWidth maxWidth="sm">
                <DialogContent sx={{ position: 'relative', height: 400, bgcolor: '#222' }}>
                    <Cropper image={imageToCrop} crop={crop} zoom={zoom} aspect={1}
                             onCropChange={setCrop}
                             onCropComplete={(_, p) => setCroppedAreaPixels(p)}
                             onZoomChange={setZoom} />
                </DialogContent>
                <Box sx={{ p: 2.5 }}>
                    <Typography gutterBottom variant="body2" fontWeight={600}>{t('profile.zoom')}</Typography>
                    <Slider value={zoom} min={1} max={3} step={0.05}
                            onChange={(_, z) => setZoom(z)} color="primary" />
                </Box>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setImageToCrop(null)}>{t('common.cancel')}</Button>
                    <Button variant="contained" onClick={applyAndUploadCrop}>{t('profile.apply_upload')}</Button>
                </DialogActions>
            </Dialog>
            <ChangePasswordDialog open={pwdDialogOpen} onClose={() => setPwdDialogOpen(false)} />
        </Box>
    );
};

async function getCroppedImg(imageSrc, pixelCrop) {
    const image = new Image();
    image.src = imageSrc;
    await new Promise(resolve => (image.onload = resolve));
    const canvas = document.createElement('canvas');
    canvas.width  = pixelCrop.width;
    canvas.height = pixelCrop.height;
    canvas.getContext('2d').drawImage(
        image,
        pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height,
        0, 0, pixelCrop.width, pixelCrop.height
    );
    return new Promise(resolve =>
        canvas.toBlob(blob => resolve(new File([blob], "profile.jpeg", { type: "image/jpeg" })), 'image/jpeg')
    );
}

export default Profile;